import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Text } from './text';
import { Button } from './button';
import { Icon } from './icon';
import { Preloader } from './preloader';

/*
    Search field: icon + input + children (usually the clear
    button row). Cmd/Ctrl+F focuses; Escape clears or blurs.
    Port of ~co/common/form/search.
*/
export const Search = React.forwardRef(function Search(
  {
    autoFocus = false,
    clearOnEscape = true,
    value = '',
    onReset,
    onChange,
    onFocus,
    onBlur,
    onKeyDown,
    loading,
    children,
    className = '',
    ...original
  },
  forwardedRef,
) {
  const [focus, setFocus] = useState(false);
  const _input = useRef(null);

  const bindRef = useCallback(
    (ref) => {
      _input.current = ref;

      if (forwardedRef) {
        if (typeof forwardedRef == 'function') forwardedRef(ref);
        else forwardedRef.current = ref;
      }
    },
    [forwardedRef],
  );

  const onResetAll = useCallback(() => {
    _input.current.value = '';
    onChange({ target: _input.current });
    onReset && onReset();
  }, [onChange, onReset]);

  const onCancelClick = useCallback(() => {
    onResetAll();
    _input.current.focus();
  }, [onResetAll]);

  const onInputFocus = useCallback(
    (e) => {
      setFocus(true);
      onFocus && onFocus(e);
    },
    [onFocus],
  );

  const onInputBlur = useCallback(
    (e) => {
      setFocus(false);
      onBlur && onBlur(e);
    },
    [onBlur],
  );

  const onInputKeyDown = useCallback(
    (e) => {
      switch (e.key) {
        case 'Escape':
          if (clearOnEscape) {
            if (value) {
              e.preventDefault();
              e.stopPropagation();
              onResetAll();
            } else e.target.blur();
          }
          break;
      }

      onKeyDown && onKeyDown(e);
    },
    [clearOnEscape, value, onResetAll, onKeyDown],
  );

  useEffect(() => {
    const onWindowKeyDown = (e) => {
      if (e.key == 'f' && (e.ctrlKey || e.metaKey)) {
        e.stopPropagation();

        if (document.activeElement != _input.current) {
          e.preventDefault();
          _input.current.focus();
        }
      }
    };

    if (autoFocus)
      _input.current && _input.current.value && _input.current.select();

    window.addEventListener('keydown', onWindowKeyDown);
    return () => window.removeEventListener('keydown', onWindowKeyDown);
  }, [autoFocus]);

  return (
    <div data-active={focus} data-clear-on-escape={clearOnEscape}>
      <Text
        ref={bindRef}
        role='searchbox'
        type='text'
        spellCheck='false'
        autoComplete='hidden'
        autoCorrect='off'
        inputMode='search'
        className={'ui-search-input ' + className}
        tabIndex='0'
        {...original}
        autoFocus={autoFocus}
        value={value}
        onChange={onChange}
        icon={loading ? <Preloader /> : <Icon name='search' />}
        onFocus={onInputFocus}
        onBlur={onInputBlur}
        onKeyDown={onInputKeyDown}
      >
        <span className='inline-flex items-center'>
          {children}
          {!!value && (
            <Button
              variant='ghost'
              size='icon'
              tabIndex='-1'
              onClick={onCancelClick}
            >
              <Icon name='close' size='micro' />
            </Button>
          )}
        </span>
      </Text>
    </div>
  );
});
